/**
 * 앱 네이티브 "리소스 단독 업로드" 브릿지 헬퍼
 *
 *  웹뷰(<input type=file>)가 사진의 content:// URI 를 직접 멀티파트로 스트리밍하다
 *  구버전 WebView 에서 실패하는 문제를 우회하기 위해, 앱 안에서는 네이티브 브릿지로
 *  이미지를 먼저 업로드하고 fileSeq 를 받아 createPet 등에 넘긴다.
 *
 *  네이티브 계약:
 *   - Android : window.mung.uploadResource(requestId, uploadPath, allowedMediaType)
 *   - iOS     : window.webkit.messageHandlers.mung.postMessage(["uploadResource", requestId, uploadPath, allowedMediaType])
 *   - 공통 콜백: window.onResourceUploaded(requestId, resultJson)
 *       resultJson: {"success":true,"fileSeq":123,"fileUrl":"..."} 또는 {"success":false,"error":"..."}
 *
 *  일반 브라우저(앱 아님)에서는 사용하지 않는다 → canUseResourceBridge() 로 분기.
 */
import { isMungApp, isMungAppIOS, isMungAppAndroid } from "./externalBrowser";
import { parseNativeAppInfo, type NativeAppPlatform } from "./appVersion";

/**
 * uploadResource 브릿지가 포함된 "최소 앱 버전".
 *  - 정책: 앱 버전 1.0.0 미만은 기존 파일입력 경로 유지, 1.0.0 이상만 네이티브 브릿지 사용.
 *  - iOS/Android 공통으로 버전 게이팅한다(안드는 함수존재 AND 버전으로 이중 확인).
 *    (안드는 함수존재만으로도 감지 가능하나, "1.0.0 미만은 기존 유지" 정책을 위해 버전도 함께 본다.)
 *  ⚠️ 네이티브 핸들러가 포함된 첫 배포 빌드는 반드시 버전을 1.0.0 이상으로 올려야 켜진다.
 */
const MIN_BRIDGE_APP_VERSION: { android: string; ios: string } = {
  android: "1.0.0",
  ios: "1.0.0",
};

/** semver 비교: v >= min 이면 true */
const isVersionGte = (v: string | null, min: string): boolean => {
  if (!v) return false;
  const a = v.split(".").map((n) => parseInt(n, 10) || 0);
  const b = min.split(".").map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const x = a[i] || 0;
    const y = b[i] || 0;
    if (x !== y) return x > y;
  }
  return true;
};

export interface ResourceUploadResult {
  fileSeq: number;
  fileUrl: string;
}

interface Pending {
  resolve: (r: ResourceUploadResult) => void;
  reject: (e: Error) => void;
}

const pendingMap: Record<string, Pending> = {};
let callbackRegistered = false;

/** 네이티브 → 웹 결과 콜백을 1회 등록 (window.onResourceUploaded) */
function ensureCallback() {
  if (callbackRegistered) return;
  callbackRegistered = true;
  (window as any).onResourceUploaded = (requestId: string, json: string) => {
    const p = pendingMap[requestId];
    if (!p) return;
    delete pendingMap[requestId];
    try {
      const parsed = JSON.parse(json);
      if (parsed && parsed.success) {
        p.resolve({
          fileSeq: Number(parsed.fileSeq),
          fileUrl: String(parsed.fileUrl ?? ""),
        });
      } else {
        p.reject(new Error(parsed?.error || "업로드에 실패했습니다."));
      }
    } catch (e) {
      p.reject(e instanceof Error ? e : new Error("업로드 결과 파싱 실패"));
    }
  };
}

/**
 * 현재 환경에서 네이티브 리소스 업로드 브릿지를 쓸 수 있는지.
 *  정책: 앱 버전 1.0.0 미만은 항상 false(기존 파일입력 경로), 1.0.0 이상만 브릿지 사용.
 *  - Android : window.mung.uploadResource 함수 존재 AND 버전 >= 1.0.0.
 *  - iOS     : mung 핸들러 존재 AND 버전 >= 1.0.0 (구버전은 핸들러가 있어도 버전 미달로 false).
 *  → 구버전(1.0.0 미만) 사용자는 항상 false 가 되어 기존 파일입력 경로로 안전하게 동작.
 */
export const canUseResourceBridge = (): boolean => {
  if (!isMungApp()) return false;
  const w = window as any;
  const info = parseNativeAppInfo();

  if (isMungAppAndroid()) {
    return (
      typeof w.mung?.uploadResource === "function" &&
      isVersionGte(info.version, MIN_BRIDGE_APP_VERSION.android)
    );
  }
  if (isMungAppIOS()) {
    const hasHandler = typeof w.webkit?.messageHandlers?.mung?.postMessage === "function";
    return hasHandler && isVersionGte(info.version, MIN_BRIDGE_APP_VERSION.ios);
  }
  return false;
};

/**
 * 네이티브 브릿지로 이미지 1장을 업로드하고 fileSeq 를 받는다.
 * @param uploadPath 서버 업로드 경로 (예: "/api/user/mypage/upload-image")
 */
export const uploadResourceViaBridge = (
  uploadPath: string,
  // "image": 사진 피커(리사이즈 JPEG) · "media": 사진+영상 피커(원본 업로드, 공지 첨부용)
  //  · "document": 문서+이미지 파일 피커(원본, 공지 첨부용)
  allowedMediaType: "image" | "media" | "document" = "image",
  timeoutMs = 120000,
): Promise<ResourceUploadResult> => {
  ensureCallback();
  return new Promise<ResourceUploadResult>((resolve, reject) => {
    const requestId = `res_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const timer = setTimeout(() => {
      if (pendingMap[requestId]) {
        delete pendingMap[requestId];
        reject(new Error("업로드 시간이 초과되었습니다. 다시 시도해 주세요."));
      }
    }, timeoutMs);
    pendingMap[requestId] = {
      resolve: (r) => {
        clearTimeout(timer);
        resolve(r);
      },
      reject: (e) => {
        clearTimeout(timer);
        reject(e);
      },
    };

    const w = window as any;
    try {
      if (isMungAppAndroid() && w.mung?.uploadResource) {
        w.mung.uploadResource(requestId, uploadPath, allowedMediaType);
      } else if (isMungAppIOS() && w.webkit?.messageHandlers?.mung) {
        w.webkit.messageHandlers.mung.postMessage([
          "uploadResource",
          requestId,
          uploadPath,
          allowedMediaType,
        ]);
      } else {
        delete pendingMap[requestId];
        clearTimeout(timer);
        reject(new Error("네이티브 업로드 브릿지를 사용할 수 없습니다."));
      }
    } catch (e) {
      delete pendingMap[requestId];
      clearTimeout(timer);
      reject(e instanceof Error ? e : new Error("브릿지 호출 실패"));
    }
  });
};

/* ─────────────────────────────────────────────────────────────────
 *  앱 버전 조회 브릿지 — 플랫폼별로 방식이 달라 내부에서 분기, 결과는 통일
 *   - Android : window.mung.getAppVersion() 동기 반환
 *   - iOS     : postMessage(["getAppVersion", requestId]) → window.onAppVersion 콜백
 *   - 앱 아님/브릿지 없음 : UA 파싱값(parseNativeAppInfo) 폴백
 * ───────────────────────────────────────────────────────────────── */
export interface AppVersionInfo {
  version: string | null;
  build: number | null;
  platform: NativeAppPlatform | null;
}

let appVersionCallbackRegistered = false;
const appVersionPending: Record<
  string,
  (r: { version: string; build: string }) => void
> = {};

function ensureAppVersionCallback() {
  if (appVersionCallbackRegistered) return;
  appVersionCallbackRegistered = true;
  (window as any).onAppVersion = (requestId: string, json: string) => {
    const cb = appVersionPending[requestId];
    if (!cb) return;
    delete appVersionPending[requestId];
    try {
      cb(JSON.parse(json));
    } catch {
      cb({ version: "", build: "" });
    }
  };
}

/** 앱 버전 조회(브릿지 우선, 실패 시 UA 폴백). 플랫폼 구분은 내부에서 처리. */
export const getAppVersionViaBridge = (
  timeoutMs = 3000,
): Promise<AppVersionInfo> => {
  const w = window as any;
  const ua = parseNativeAppInfo(); // 폴백/기본값

  // Android — 동기 반환
  if (isMungAppAndroid() && typeof w.mung?.getAppVersion === "function") {
    try {
      const v = w.mung.getAppVersion();
      return Promise.resolve({
        version: (typeof v === "string" && v) || ua.version,
        build: ua.build,
        platform: "android",
      });
    } catch {
      return Promise.resolve({
        version: ua.version,
        build: ua.build,
        platform: "android",
      });
    }
  }

  // iOS — 콜백
  if (isMungAppIOS() && w.webkit?.messageHandlers?.mung?.postMessage) {
    ensureAppVersionCallback();
    return new Promise<AppVersionInfo>((resolve) => {
      const requestId = `ver_${Date.now()}_${Math.random().toString(36).slice(2)}`;
      const timer = setTimeout(() => {
        if (appVersionPending[requestId]) {
          delete appVersionPending[requestId];
          resolve({ version: ua.version, build: ua.build, platform: "ios" });
        }
      }, timeoutMs);
      appVersionPending[requestId] = (r) => {
        clearTimeout(timer);
        resolve({
          version: r.version || ua.version,
          build: r.build ? parseInt(r.build, 10) || ua.build : ua.build,
          platform: "ios",
        });
      };
      try {
        w.webkit.messageHandlers.mung.postMessage(["getAppVersion", requestId]);
      } catch {
        clearTimeout(timer);
        delete appVersionPending[requestId];
        resolve({ version: ua.version, build: ua.build, platform: "ios" });
      }
    });
  }

  // 앱 아님 / 브릿지 미지원(구버전) → UA 파싱값
  return Promise.resolve({
    version: ua.version,
    build: ua.build,
    platform: ua.platform,
  });
};
